import { useHandler } from "@redotech/react-util/hook";
import CloseIcon from "@redotech/redo-icons/x-close.svg";
import classNames from "classnames";
import { memo, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { RedoButton } from "../arbiter-components/buttons/redo-button";
import {
  RedoCheckbox,
  type CheckboxValue,
} from "../arbiter-components/checkbox/redo-checkbox";
import { Flex } from "../flex";
import { Text } from "../text";
import * as css from "./arbiter-design-system-inspector.module.css";
import {
  ADSI_STYLE_ELEMENT_ID,
  SHORTCUT_IDLE_MS,
  advanceShortcutBuffer,
  canUseDesignSystemInspector,
  clampDialogPosition,
  designSystemInspectorCss,
  designSystemLayers,
  isTextEntryTarget,
  type DesignSystemLayer,
} from "./design-system-inspector";

const swatchClassName: Record<DesignSystemLayer, string | undefined> = {
  arbiter: css.arbiterSwatch,
  flex: css.flexSwatch,
  "text-header": css.textHeaderSwatch,
  web: css.webSwatch,
};

export const ArbiterDesignSystemInspector = memo(
  function ArbiterDesignSystemInspector({ email }: { email?: string | null }) {
    const allowed = canUseDesignSystemInspector(email);
    const [open, setOpen] = useState(false);
    const [enabledLayers, setEnabledLayers] = useState<
      ReadonlySet<DesignSystemLayer>
    >(() => new Set());
    const [position, setPosition] = useState<
      { left: number; top: number } | undefined
    >();
    const dialogRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef<
      { pointerId: number; offsetX: number; offsetY: number } | undefined
    >();

    useEffect(() => {
      if (!allowed) {
        setOpen(false);
        return;
      }
      let buffer = "";
      let idleTimer: ReturnType<typeof setTimeout> | undefined;

      function onKeyDown(event: KeyboardEvent) {
        if (event.repeat || event.metaKey || event.ctrlKey || event.altKey) {
          return;
        }
        if (isTextEntryTarget(event.target)) {
          buffer = "";
          return;
        }
        const result = advanceShortcutBuffer(buffer, event.key);
        buffer = result.buffer;
        if (idleTimer) {
          clearTimeout(idleTimer);
          idleTimer = undefined;
        }
        if (buffer) {
          idleTimer = setTimeout(() => {
            buffer = "";
          }, SHORTCUT_IDLE_MS);
        }
        if (result.matched) {
          event.preventDefault();
          setOpen((current) => !current);
        }
      }

      window.addEventListener("keydown", onKeyDown, true);
      return () => {
        window.removeEventListener("keydown", onKeyDown, true);
        if (idleTimer) {
          clearTimeout(idleTimer);
        }
      };
    }, [allowed]);

    useEffect(() => {
      if (!allowed || !open) {
        return;
      }
      const style = document.createElement("style");
      style.id = ADSI_STYLE_ELEMENT_ID;
      style.textContent = designSystemInspectorCss(enabledLayers);
      document.head.append(style);
      return () => {
        style.remove();
      };
    }, [allowed, enabledLayers, open]);

    const close = useHandler(() => {
      setOpen(false);
    });

    const setLayer = useHandler(
      (layer: DesignSystemLayer, value: CheckboxValue) => {
        setEnabledLayers((current) => {
          const next = new Set(current);
          if (value === true) {
            next.add(layer);
          } else {
            next.delete(layer);
          }
          return next;
        });
      },
    );

    const endDrag = useHandler((event: React.PointerEvent<HTMLDivElement>) => {
      if (dragRef.current?.pointerId !== event.pointerId) {
        return;
      }
      dragRef.current = undefined;
    });

    const onHeaderPointerDown = useHandler(
      (event: React.PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) {
          return;
        }
        if (event.target instanceof Element && event.target.closest("button")) {
          return;
        }
        const dialog = dialogRef.current;
        if (!dialog) {
          return;
        }
        const rect = dialog.getBoundingClientRect();
        dragRef.current = {
          offsetX: event.clientX - rect.left,
          offsetY: event.clientY - rect.top,
          pointerId: event.pointerId,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
        event.preventDefault();
      },
    );

    const onHeaderPointerMove = useHandler(
      (event: React.PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        const dialog = dialogRef.current;
        if (!drag || drag.pointerId !== event.pointerId || !dialog) {
          return;
        }
        setPosition(
          clampDialogPosition(
            event.clientX - drag.offsetX,
            event.clientY - drag.offsetY,
            dialog.offsetWidth,
            { height: window.innerHeight, width: window.innerWidth },
          ),
        );
      },
    );

    if (!allowed || !open) {
      return null;
    }

    return createPortal(
      <Flex
        aria-label="Arbiter design system inspector"
        bgColor="primary"
        borderColor="secondary"
        borderRadius="md"
        borderStyle="solid"
        borderWidth="1px"
        className={css.dialog}
        data-testid="arbiter-design-system-inspector"
        dir="column"
        gap="none"
        overflow="hidden"
        position="fixed"
        ref={dialogRef}
        role="dialog"
        shadow="lg"
        {...(position
          ? { style: { left: position.left, top: position.top } }
          : { right: "xl" as const, top: "xl" as const })}
      >
        <Flex
          align="center"
          className={css.header}
          justify="space-between"
          onPointerCancel={endDrag}
          onPointerDown={onHeaderPointerDown}
          onPointerMove={onHeaderPointerMove}
          onPointerUp={endDrag}
          px="lg"
          py="sm"
        >
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            Arbiter design system inspector
          </Text>
          <RedoButton
            aria-label="Close"
            dataTestId="adsi-close"
            hierarchy="tertiary"
            IconLeading={CloseIcon}
            onClick={close}
            size="sm"
          />
        </Flex>
        <Flex dir="column" gap="md" p="lg">
          {designSystemLayers.map((layer) => (
            <Flex align="center" gap="md" key={layer.id}>
              <Flex
                aria-hidden
                className={classNames(css.swatch, swatchClassName[layer.id])}
              />
              <RedoCheckbox
                dataTestId={`adsi-${layer.id}`}
                label={layer.label}
                setValue={(value) => setLayer(layer.id, value)}
                size="sm"
                value={enabledLayers.has(layer.id)}
              />
            </Flex>
          ))}
        </Flex>
      </Flex>,
      document.body,
    );
  },
);
